import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import {
  findTradeBySlug,
  findNeighborhoodBySlug,
  buildVendorPageTitle,
  buildVendorMetaDescription,
  buildVendorPageJsonLd,
  vendorPageUrl,
  costPageUrl,
} from "../../lib/seo/seoTargetMatrix";
import { SEO_SERVICES, STL_NEIGHBORHOODS } from "../../lib/seo/seoTargetMatrix";

// ─── Styled components ────────────────────────────────────────────────────────

const Page = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    sans-serif;
  color: #1a1a2e;
  background: #fff;
  min-height: 100vh;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #0f3460 0%, #16213e 60%, #1a1a2e 100%);
  color: #fff;
  padding: 72px 24px 64px;
  text-align: center;
`;

const HeroEyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #ff7043;
  margin: 0 0 16px;
`;

const HeroH1 = styled.h1`
  font-size: clamp(24px, 5vw, 42px);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 20px;
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroSubline = styled.p`
  font-size: clamp(15px, 2.5vw, 18px);
  color: rgba(255, 255, 255, 0.78);
  margin: 0 0 40px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.55;
`;

const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ff5722;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 16px 32px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  &:hover {
    background: #e64a19;
    transform: translateY(-1px);
  }
`;

const SearchFeeNote = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 14px 0 0;
`;

const TrustRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px 36px;
  margin-top: 44px;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);

  span:first-child {
    font-size: 18px;
  }
`;

const Section = styled.section`
  padding: 56px 24px;
  border-bottom: 1px solid #f0f0f0;
  max-width: 860px;
  margin: 0 auto;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionLabel = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #ff5722;
  margin: 0 0 8px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 700;
  margin: 0 0 28px;
  color: #1a1a2e;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
`;

const ProcessStep = styled.div`
  background: #f8f9fa;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  padding: 24px;
`;

const StepNum = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #ff5722;
  color: #fff;
  font-weight: 800;
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const StepTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  margin: 0 0 8px;
  color: #1a1a2e;
`;

const StepDesc = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.55;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const ServiceCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #ff5722;
    box-shadow: 0 2px 10px rgba(255, 87, 34, 0.1);
  }
`;

const ServiceCardName = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #1a1a2e;
`;

const ServiceCardRange = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const CoverageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CoverageChip = styled(Link)`
  display: inline-block;
  padding: 7px 14px;
  background: #f8f9fa;
  border: 1px solid #e8ecef;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #ff5722;
    background: #fff5f2;
    color: #ff5722;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  overflow: hidden;
`;

const FaqItem = styled.div`
  border-bottom: 1px solid #e8ecef;
  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 18px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  line-height: 1.4;
  &:hover {
    background: #f8f9fa;
  }
`;

const FaqChevron = styled.span`
  flex-shrink: 0;
  font-size: 18px;
  color: #ff5722;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
`;

const FaqAnswer = styled.div`
  display: ${({ $open }) => ($open ? "block" : "none")};
  padding: 4px 20px 20px;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.65;
`;

const FooterCtaBox = styled.section`
  background: linear-gradient(135deg, #0f3460, #1a1a2e);
  color: #fff;
  text-align: center;
  padding: 64px 24px;
`;

const FooterCtaTitle = styled.h2`
  font-size: clamp(22px, 3.5vw, 34px);
  font-weight: 700;
  margin: 0 0 14px;
`;

const FooterCtaSubline = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 32px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

const VENDOR_FAQ = [
  {
    question: "How does Clumse vet contractors before matching them?",
    answer:
      "Clumse verifies license status, insurance coverage, and reviews references before adding any contractor to the matching network. Contractors are also rated after each completed job - those who underperform are removed from active matching.",
  },
  {
    question: "Do I get at least 3 bids, or could I get fewer?",
    answer:
      "Clumse guarantees outreach to a minimum of 3 qualified contractors in your area. In most St. Louis-area ZIP codes, we have 5–10 contractors in each trade category. You'll receive bids from those who respond to the outreach - typically 2–4 within 3–5 business days.",
  },
  {
    question: "Does Clumse take a commission from the contractor I hire?",
    answer:
      "No. Clumse earns only the upfront search fee paid by the homeowner. Contractors quote their standard market rates - no markup is added by Clumse, and no referral fee is charged on the job after hiring.",
  },
];

function FaqAccordion({ items }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <FaqList>
      {items.map((item, i) => (
        <FaqItem key={i}>
          <FaqQuestion onClick={() => setOpenIdx(openIdx === i ? null : i)}>
            {item.question}
            <FaqChevron $open={openIdx === i}>▾</FaqChevron>
          </FaqQuestion>
          <FaqAnswer $open={openIdx === i}>{item.answer}</FaqAnswer>
        </FaqItem>
      ))}
    </FaqList>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function VendorLandingPage() {
  const { trade: tradeSlug, neighborhoodSlug } = useParams();
  const navigate = useNavigate();

  const trade = findTradeBySlug(tradeSlug);
  const neighborhood = findNeighborhoodBySlug(neighborhoodSlug);

  if (!trade || !neighborhood) {
    return (
      <Page>
        <Helmet>
          <title>Not Found | Clumse</title>
        </Helmet>
        <Section style={{ textAlign: "center", paddingTop: 100 }}>
          <SectionTitle>Page not found</SectionTitle>
          <p style={{ color: "#6b7280" }}>
            We don't have a vendor guide for that combination yet.
          </p>
          <Link to="/" style={{ color: "#ff5722", fontWeight: 600 }}>
            ← Back to Clumse
          </Link>
        </Section>
      </Page>
    );
  }

  const pageTitle = buildVendorPageTitle(trade, neighborhood);
  const metaDesc = buildVendorMetaDescription(trade, neighborhood);
  const jsonLd = buildVendorPageJsonLd(trade, neighborhood);
  const canonicalUrl = `https://clumse.com${vendorPageUrl(trade.slug, neighborhood.slug)}`;

  // Services handled by this trade
  const tradeServices = SEO_SERVICES.filter((s) =>
    trade.services.includes(s.slug)
  );

  // Nearby neighborhoods
  const nearbyNeighborhoods = STL_NEIGHBORHOODS.filter(
    (n) => n.slug !== neighborhood.slug
  ).slice(0, 8);

  const chatUrl = `/chat?preMessage=${encodeURIComponent(
    `I'm looking for a ${trade.name.toLowerCase()} in ${neighborhood.name} (${neighborhood.zip}).`
  )}&zip=${neighborhood.zip}`;

  return (
    <Page>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {jsonLd && (
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <Hero>
        <HeroEyebrow>
          {neighborhood.name} · {neighborhood.zip} · {neighborhood.region}
        </HeroEyebrow>

        <HeroH1>
          Find Vetted {trade.displayTitle} in {neighborhood.name}
        </HeroH1>

        <HeroSubline>
          {trade.description} Clumse pre-scopes your project, then matches you
          with licensed, insured contractors in {neighborhood.zip} - so you
          compare real bids, not guesses.
        </HeroSubline>

        <CtaButton onClick={() => navigate(chatUrl)}>
          {trade.ctaLabel} →
        </CtaButton>

        <SearchFeeNote>
          Flat $49 search fee · 3 vetted bids guaranteed · No commitment
        </SearchFeeNote>

        <TrustRow>
          <TrustItem>
            <span>✓</span> Licensed &amp; insured
          </TrustItem>
          <TrustItem>
            <span>✓</span> Background checked
          </TrustItem>
          <TrustItem>
            <span>✓</span> Rated after every job
          </TrustItem>
          <TrustItem>
            <span>✓</span> Direct contact - no middleman
          </TrustItem>
        </TrustRow>
      </Hero>

      {/* ── How it works ─────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Process</SectionLabel>
        <SectionTitle>How Clumse Matches You With {trade.displayTitle}</SectionTitle>
        <ProcessGrid>
          <ProcessStep>
            <StepNum>1</StepNum>
            <StepTitle>Describe your project</StepTitle>
            <StepDesc>
              Clumse AI collects the details - scope, timeline, property type - and builds a structured brief that gets sent to contractors. No
              forms, no back-and-forth.
            </StepDesc>
          </ProcessStep>
          <ProcessStep>
            <StepNum>2</StepNum>
            <StepTitle>Clumse reaches out to 3+ contractors</StepTitle>
            <StepDesc>
              Your brief goes to vetted {trade.name.toLowerCase()}s in{" "}
              {neighborhood.zip}. Each one has been license-verified and
              background-checked before joining the Clumse network.
            </StepDesc>
          </ProcessStep>
          <ProcessStep>
            <StepNum>3</StepNum>
            <StepTitle>Compare bids and hire direct</StepTitle>
            <StepDesc>
              Contractors contact you directly with detailed quotes. You
              compare, choose, and hire - Clumse steps aside. No commissions
              added to the contractor's fee.
            </StepDesc>
          </ProcessStep>
        </ProcessGrid>
      </Section>

      {/* ── Services this trade covers ────────────────────────────── */}
      {tradeServices.length > 0 && (
        <Section>
          <SectionLabel>Services</SectionLabel>
          <SectionTitle>
            {trade.displayTitle} in {neighborhood.name} Handle
          </SectionTitle>
          <ServiceGrid>
            {tradeServices.map((s) => (
              <ServiceCard
                key={s.slug}
                to={costPageUrl(s.slug, neighborhood.zip)}
              >
                <ServiceCardName>{s.name}</ServiceCardName>
                <ServiceCardRange>
                  ${s.priceRangeLow.toLocaleString()} – ${s.priceRangeHigh.toLocaleString()} typical
                </ServiceCardRange>
              </ServiceCard>
            ))}
          </ServiceGrid>
        </Section>
      )}

      {/* ── Coverage area ─────────────────────────────────────────── */}
      <Section>
        <SectionLabel>Coverage</SectionLabel>
        <SectionTitle>
          {trade.displayTitle} Available Throughout St. Louis
        </SectionTitle>
        <p style={{ color: "#4b5563", marginBottom: 20, lineHeight: 1.6 }}>
          Clumse matches {trade.name.toLowerCase()}s across the St. Louis metro
          area. Browse other neighborhoods:
        </p>
        <CoverageGrid>
          {nearbyNeighborhoods.map((n) => (
            <CoverageChip
              key={n.slug}
              to={vendorPageUrl(trade.slug, n.slug)}
            >
              {n.name} ({n.zip})
            </CoverageChip>
          ))}
        </CoverageGrid>
      </Section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <Section>
        <SectionLabel>FAQ</SectionLabel>
        <SectionTitle>
          Common Questions About Finding {trade.displayTitle}
        </SectionTitle>
        <FaqAccordion items={VENDOR_FAQ} />
      </Section>

      {/* ── Footer CTA ────────────────────────────────────────────── */}
      <FooterCtaBox>
        <FooterCtaTitle>
          Get {trade.displayTitle} Quotes in {neighborhood.name}
        </FooterCtaTitle>
        <FooterCtaSubline>
          Tell Clumse what you need. We'll pre-scope the project and match you
          with licensed {trade.name.toLowerCase()}s in {neighborhood.zip} - typically within 24 hours.
        </FooterCtaSubline>
        <CtaButton onClick={() => navigate(chatUrl)}>
          {trade.ctaLabel} →
        </CtaButton>
        <SearchFeeNote style={{ color: "rgba(255,255,255,0.45)" }}>
          Flat $49 search fee · No commitment · Cancel anytime before payment
        </SearchFeeNote>
      </FooterCtaBox>
    </Page>
  );
}
